import React, { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";

export default function BusinessLogo() {
  const { businessId, businessName, businessHasLogo, refetchVenues, user, isAdmin } = useDash();
  const canEdit = user?.role === "owner" || isAdmin;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);
  const hasLogo = businessHasLogo || bust > 0;
  const logoUrl = `${API}/business/${businessId}/logo?v=${bust}`;

  const pick = () => { if (canEdit && !uploading) inputRef.current?.click(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(f.type)) { toast.error("Please choose a PNG or JPG image."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/business/logo", fd, { params: businessId ? { business_id: businessId } : {}, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      setBust(Date.now());
      refetchVenues?.();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not upload logo");
    } finally { setUploading(false); }
  };

  return (
    <div className="mb-5 flex items-center gap-4" data-testid="business-logo">
      <button onClick={pick} disabled={!canEdit} data-testid="business-logo-btn"
        className={`group relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-white bg-cloud shadow-pulse-sm ring-1 ring-slate-200 ${canEdit ? "cursor-pointer" : "cursor-default"}`}>
        {hasLogo ? (
          <img src={logoUrl} alt={businessName} className="h-full w-full object-cover" />
        ) : (
          <Camera size={22} className="text-slate" />
        )}
        {canEdit && (
          <span className={`absolute inset-0 place-items-center bg-black/45 text-white ${uploading ? "grid" : "hidden group-hover:grid"}`}>
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
          </span>
        )}
      </button>
      <div>
        <div className="text-lg font-bold text-pulse-navy" data-testid="business-logo-name">{businessName || "Your business"}</div>
        {canEdit ? (
          <button onClick={pick} data-testid="business-logo-text" className="text-sm font-medium text-pulse-green hover:underline">
            {hasLogo ? "Change logo" : "Set up your logo"}
          </button>
        ) : (
          <div className="text-sm text-slate">Bookings dashboard</div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid="business-logo-input" />
    </div>
  );
}
